import { Trans } from '@lingui/react/macro';

import { InputSearchSelect } from '@/design_system/InputSearchSelect/InputSearchSelect';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import { getRootProductAttributes, useProductAttributes } from '@/models/productAttribute';
import { I18nWording, useDatabaseTranslation } from '@/services/i18n';
import { unique } from '@/utils/array';

export type ProductAttributeValue = {
  id: string;
  name: I18nWording;
  productAttribute: {
    id: string;
    name: I18nWording;
    parentProductAttributeValueId: string | null;
  };
};

export const ProductAttributeValuesInput = ({
  organizationId,
  selectedProductAttributeValues,
  setSelectedProductAttributeValues,
}: {
  organizationId: string;
  selectedProductAttributeValues: ProductAttributeValue[];
  setSelectedProductAttributeValues: (productAttributeValues: ProductAttributeValue[]) => void;
}) => {
  const { data: { productAttributes = [] } = { productAttributes: [] } } = useProductAttributes({
    organizationId,
  });

  const rootProductAttributes = getRootProductAttributes({ productAttributes });

  return (
    <Stack gap="0.5rem">
      <Paragraph weight="medium">
        <Trans id="settings.organization.data-catalogs.condition-forms.detail.question.product-attribute-values.label">
          Product attributes:
        </Trans>
      </Paragraph>
      {rootProductAttributes.map((attribute) => (
        <ProductAttributeNode
          key={attribute.id}
          attribute={attribute}
          productAttributes={productAttributes}
          selectedProductAttributeValues={selectedProductAttributeValues}
          setSelectedProductAttributeValues={setSelectedProductAttributeValues}
        />
      ))}
    </Stack>
  );
};

type ProductAttribute = ReturnType<typeof getRootProductAttributes>[number];

const ProductAttributeNode = ({
  attribute,
  productAttributes,
  selectedProductAttributeValues,
  setSelectedProductAttributeValues,
}: {
  attribute: ProductAttribute;
  productAttributes: ProductAttribute[];
  selectedProductAttributeValues: ProductAttributeValue[];
  setSelectedProductAttributeValues: (productAttributeValues: ProductAttributeValue[]) => void;
}) => {
  const { _db } = useDatabaseTranslation();

  const attributeSelectedProductAttributeValues = attribute.productAttributeValues.filter(
    (productAttributeValue) =>
      selectedProductAttributeValues.some((pav) => pav.id === productAttributeValue.id)
  );

  return (
    <Stack gap="0.5rem">
      <Stack row gap="0.5rem" alignItems="center">
        <Paragraph>{_db(attribute.name)}:</Paragraph>
        <InputSearchSelect
          isMulti
          size="small"
          aria-label={_db(attribute.name)}
          placeholder={_db(attribute.name)}
          value={attributeSelectedProductAttributeValues}
          options={attribute.productAttributeValues}
          getOptionValue={(option) => option.id}
          getOptionLabel={(option) => _db(option.name)}
          onChange={(options) => {
            setSelectedProductAttributeValues(
              unique([...selectedProductAttributeValues, ...options], 'id')
            );
          }}
          onRemoveValue={(id) => {
            setSelectedProductAttributeValues(
              selectedProductAttributeValues.filter((value) => value.id !== id)
            );
          }}
          style={{ flex: 1 }}
        />
      </Stack>
      {attributeSelectedProductAttributeValues.map((productAttributeValue) => (
        <ProductAttributeValueNode
          key={productAttributeValue.id}
          attributeValue={productAttributeValue}
          productAttributes={productAttributes}
          selectedProductAttributeValues={selectedProductAttributeValues}
          setSelectedProductAttributeValues={setSelectedProductAttributeValues}
        />
      ))}
    </Stack>
  );
};

const ProductAttributeValueNode = ({
  attributeValue,
  productAttributes,
  selectedProductAttributeValues,
  setSelectedProductAttributeValues,
}: {
  attributeValue: ProductAttributeValue;
  productAttributes: ProductAttribute[];
  selectedProductAttributeValues: ProductAttributeValue[];
  setSelectedProductAttributeValues: (productAttributeValues: ProductAttributeValue[]) => void;
}) => {
  const { _db } = useDatabaseTranslation();

  const childProductAttributes = productAttributes.filter(
    (attribute) => attribute.parentProductAttributeValueId === attributeValue.id
  );

  if (childProductAttributes.length === 0) {
    return null;
  }

  return (
    <Stack gap="0.5rem" padding="0 0 0 1rem">
      <Paragraph weight="heavy">{_db(attributeValue.name)}:</Paragraph>
      <Stack gap="0.25rem" padding="0 0 0 1rem">
        {childProductAttributes.map((attribute) => (
          <ProductAttributeNode
            key={attribute.id}
            attribute={attribute}
            productAttributes={productAttributes}
            selectedProductAttributeValues={selectedProductAttributeValues}
            setSelectedProductAttributeValues={setSelectedProductAttributeValues}
          />
        ))}
      </Stack>
    </Stack>
  );
};
